// RAS-Validation. 1:1 portiert aus V1 useEveMemory.js validateRasRule/isDuplicateRasRule/detectRasContext.
// Pure functions — kein State.

import type { RasContextHints, RasRule, RasValidation } from '@/types/eve-memory';

/** V1-Kategorien die immer geladen werden (Kern-Denkregeln). */
export const ALWAYS_CATEGORIES = new Set([
  'risk_management',
  'analytical_thinking',
  'news_analysis',
  'general',
]);

/** Kategorien nur bei Kausal-Reasoning-Anfragen. */
export const CAUSAL_CATEGORIES = new Set(['causal_reasoning', 'memory_extraction']);

/** Kategorien nur bei Trade/Markt-Anfragen. */
export const MARKET_CATEGORIES = new Set(['macro_regime', 'market_structure', 'market_signals']);

/**
 * Prüft ob eine RAS-Regel speichernswert ist. V1 verlangt:
 * - String, getrimmt mind. 25 Zeichen
 * - Trigger-Bedingung ODER Aktions-Verb erkennbar
 */
export function validateRasRule(rule: unknown): RasValidation {
  if (typeof rule !== 'string') return { valid: false, reason: 'Kein String' };
  const t = rule.trim();
  if (t.length < 25) {
    return { valid: false, reason: `Zu kurz (${t.length} Zeichen) — kein verwertbares Muster` };
  }
  const hasTrigger =
    /\b(wenn|bei|nach|im|falls|vor|während|sobald|solange|nach einem|bei einem)\b/i.test(t);
  const hasAction =
    /\b(nicht|kein|keine|immer|nie|nur|muss|soll|wird|verhindert|blockiert|pausiert|abwarten|prüfen|ignorieren|beachten|einplanen)\b/i.test(
      t,
    );
  if (!hasTrigger && !hasAction) {
    return {
      valid: false,
      reason: 'Keine erkennbare Trigger-Bedingung oder Aktion — confidence zu niedrig',
    };
  }
  return { valid: true };
}

/** V1: erste 45 Zeichen (normalisiert) entscheiden über Duplikat. */
export function isDuplicateRasRule(rule: string, existing: ReadonlyArray<Pick<RasRule, 'rule'>>): boolean {
  const key = rule.toLowerCase().replace(/\s+/g, ' ').trim().slice(0, 45);
  return existing.some(
    (r) => (r.rule || '').toLowerCase().replace(/\s+/g, ' ').trim().slice(0, 45) === key,
  );
}

/**
 * V1 `detectRasContext` — Heuristik um aus der User-Message zu erkennen ob
 * KAUSAL- oder MARKT-Kategorien geladen werden müssen.
 */
export function detectRasContext(userMsg = ''): RasContextHints {
  const msg = userMsg.toLowerCase();
  const isCausal =
    /reverse|forward|mechanisch|kausal|wer startet|erste aktion|mechanical|strict reverse|kontrafakt/i.test(
      msg,
    );
  const isMarket =
    /trade|setup|btc|eth|sol|gold|oil|vix|dxy|regime|markt|chart|long|short|entry|sl|tp|scalp|swing|liquidit|funding|oi\b/i.test(
      msg,
    );
  return { isCausal, isMarket };
}
